/**
 * The three TipTap pieces the email editor needs and the ecosystem does not give us
 * in a usable version: a link mark and the two inline atoms.
 *
 * ### Why they are hand-written
 *
 * `@tiptap/core`, `react`, `pm` and `starter-kit` resolve to **2.27.2**, while the
 * four `@tiptap/extension-*` packages in `package.json` resolve to **3.27.3**. Importing
 * a v3 extension against a v2 core is exactly the arrangement the shipped lead-email
 * composer already got away with by luck, and not one to build on.
 *
 * Writing these against the v2 core that *is* installed removes the dependency on the
 * v3 packages entirely: `StarterKit` v2 already provides bold, italic, lists, history
 * and paragraphs, and everything below is v2 API. No package.json change, no install.
 *
 * ### The atoms
 *
 * A variable and a translation are single, indivisible things — you cannot put a
 * cursor inside `{{ customer.firstname }}` and edit half of it. So they are
 * `atom: true, inline: true` nodes with **no content**, rendered as chips by a node
 * view, and serialized back to exactly the element `richTextValue.ts` reads. The node
 * view is what the user sees; `renderHTML` is what the round-trip sees, and it stays
 * empty so `fromEditorHtml`'s parse cannot be confused by a label.
 */
import { Mark, Node, mergeAttributes } from '@tiptap/core';
import type { NodeViewRendererProps } from '@tiptap/core';

/**
 * Teaches TipTap's command types about the two commands below.
 *
 * Without this the commands have to be added with an `as any`, and then **every call
 * site is unchecked** — `editor.chain().setEmailLnk({...})` compiles and fails at
 * runtime. The augmentation is the supported v2 way to type a custom command.
 */
declare module '@tiptap/core' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface Commands<ReturnType> {
    emailLink: {
      /** Marks the selection as a link to `href`. */
      setEmailLink: (attributes: { href: string }) => ReturnType;
      /** Removes the link mark, whether or not the selection is empty. */
      unsetEmailLink: () => ReturnType;
    };
  }
}

/**
 * `<a href>`, with only the attributes `richTextValue.ts` allows through its gate.
 *
 * `inclusive: false`: typing at the end of a link should write plain text after it,
 * not extend the link — the opposite is a well-known way to end up with a whole
 * paragraph inside an anchor.
 */
export const EmailLink = Mark.create({
  name: 'link',
  priority: 1000,
  inclusive: false,

  addAttributes() {
    return {
      href: { default: null },
      target: { default: '_blank' },
      rel: { default: null },
    };
  },

  parseHTML() {
    return [{ tag: 'a[href]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['a', mergeAttributes(HTMLAttributes), 0];
  },

  addCommands() {
    return {
      setEmailLink:
        (attributes: { href: string }) =>
        ({ chain }) =>
          chain().setMark(this.name, attributes).run(),
      unsetEmailLink:
        () =>
        ({ chain }) =>
          // `extendEmptyMarkRange` so unsetting works with the caret merely inside
          // the link, rather than requiring the whole link to be selected.
          chain().unsetMark(this.name, { extendEmptyMarkRange: true }).run(),
    };
  },
});

/** Shared chip rendering, so a variable and a translation look like siblings. */
function chipView(className: string, label: (value: string) => string) {
  return ({ node }: NodeViewRendererProps) => {
    const dom = window.document.createElement('span');
    dom.className = className;
    dom.textContent = label(node.attrs.value ?? '');
    // Nothing inside is editable; the node is replaced or deleted as a unit.
    dom.setAttribute('contenteditable', 'false');
    return { dom };
  };
}

/** `{{ path }}` — a send-time value, shown as its last readable segment. */
export const VariableAtom = Node.create({
  name: 'cvnVariable',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      value: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-cvn-var') ?? '',
        renderHTML: (attributes) => ({ 'data-cvn-var': attributes.value }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-cvn-var]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes)];
  },

  addNodeView() {
    return chipView('cvn-chip cvn-chip-var', (value) => value);
  },
});

/** `{{ 'group.key'|trans }}` — partner-wide wording, shown by its key. */
export const TranslationAtom = Node.create({
  name: 'cvnTranslation',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      value: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-cvn-trans') ?? '',
        renderHTML: (attributes) => ({ 'data-cvn-trans': attributes.value }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-cvn-trans]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes)];
  },

  addNodeView() {
    // The `|raw` suffix is a serialization detail, not something to read on a chip.
    return chipView('cvn-chip cvn-chip-trans', (value) =>
      value.replace(/\|raw$/, ''),
    );
  },
});

/**
 * Colour, size and font for a run of text, as one mark.
 *
 * One mark rather than the ecosystem's `TextStyle` + `Color` pair, for the reason
 * the rest of this file exists: those ship as v3 packages and the installed core is
 * v2. This is the v2 API and adds no dependency.
 *
 * ### It must agree with the gate
 *
 * `richTextValue.ts` admits a `<span style>` only when every declaration is in
 * `ALLOWED_STYLE_PROPERTIES` — `color` and `font-size`. That list and this mark's
 * attributes are the same set stated twice: a property allowed there without a
 * home here is one TipTap parses and then silently drops on serialize, which is
 * the flattening the gate exists to prevent. Change them together.
 *
 * Declarations are emitted in a fixed order (colour, size, then font) so the same marks
 * always produce the same bytes. A hand-written span in the other order therefore
 * normalises to this one the first time the field is edited — the stored value
 * changes, but only its declaration order, and only on an actual edit.
 */
export const InlineTextStyle = Mark.create({
  name: 'cvnTextStyle',

  addAttributes() {
    return {
      color: {
        default: null,
        parseHTML: (element: HTMLElement) => element.style.color || null,
      },
      fontSize: {
        default: null,
        parseHTML: (element: HTMLElement) => element.style.fontSize || null,
      },
      // Read back re-quoted and re-spaced by the DOM, which is why the toolbar
      // matches it through `normalizeFontStack` rather than by string equality.
      fontFamily: {
        default: null,
        parseHTML: (element: HTMLElement) => element.style.fontFamily || null,
      },
    };
  },

  parseHTML() {
    // Only a span that *carries* one of them — a bare span is an atom's wrapper.
    return [
      {
        tag: 'span[style]',
        getAttrs: (element) => {
          const style = (element as HTMLElement).style;
          return style.color || style.fontSize || style.fontFamily
            ? {}
            : false;
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const declarations: string[] = [];
    if (HTMLAttributes.color) declarations.push(`color:${HTMLAttributes.color}`);
    if (HTMLAttributes.fontSize) {
      declarations.push(`font-size:${HTMLAttributes.fontSize}`);
    }
    if (HTMLAttributes.fontFamily) {
      declarations.push(`font-family:${HTMLAttributes.fontFamily}`);
    }
    return ['span', { style: declarations.join(';') }, 0];
  },

  addCommands() {
    return {
      setTextColor:
        (color: string | null) =>
        ({ chain }) =>
          chain().setMark(this.name, { color }).run(),
      setTextSize:
        (fontSize: string | null) =>
        ({ chain }) =>
          chain().setMark(this.name, { fontSize }).run(),
      setTextFont:
        (fontFamily: string | null) =>
        ({ chain }) =>
          chain().setMark(this.name, { fontFamily }).run(),
      clearTextStyle:
        () =>
        ({ chain }) =>
          chain().unsetMark(this.name).run(),
    } as never;
  },
});
